/**
 * Composant NotificationBadge - Icône de notification avec badge
 * Intégrée dans le TopNav
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { cn } from "@core/lib/utils";
import { NotificationBadgeState } from "@core/types/notifications";

interface NotificationBadgeProps {
  badgeState: NotificationBadgeState;
  onClick: () => void;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  badgeState,
  onClick,
  className = "",
  size = "md",
}) => {
  const { t } = useTranslation("common");
  const sizes = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const badgeSizes = {
    sm: "w-2 h-2 text-xs",
    md: "w-3 h-3 text-xs",
    lg: "w-4 h-4 text-sm",
  };

  const badgeColors = {
    red: "bg-destructive text-destructive-foreground border-destructive",
    orange: "bg-warning text-warning-foreground border-warning",
    blue: "bg-primary text-primary-foreground border-primary",
    gray: "bg-muted text-muted-foreground border-border",
  };

  const iconColors = {
    red: "text-destructive hover:text-destructive/80",
    orange: "text-warning hover:text-warning/80",
    blue: "text-primary hover:text-primary/80",
    gray: "text-muted-foreground hover:text-foreground",
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        "relative p-3 md:p-2 rounded-lg transition-all duration-200",
        "hover:bg-accent active:scale-95",
        iconColors[badgeState.color],
        className,
      )}
      aria-label={
        badgeState.count > 0
          ? `${t("notifications_title")} (${t("notifications.badge_unread", { count: badgeState.count })})`
          : t("notifications_title")
      }
    >
      <Bell className={cn(sizes[size], badgeState.pulse && "animate-pulse")} />

      {/* Badge indicateur */}
      {badgeState.visible && (
        <span
          className={cn(
            "absolute -top-1 -right-1 rounded-full flex items-center justify-center font-bold border-2 border-background",
            badgeSizes[size],
            badgeColors[badgeState.color],
            badgeState.pulse && "animate-ping",
          )}
        >
          {badgeState.count > 0
            ? badgeState.count > 99
              ? "99+"
              : badgeState.count
            : ""}
        </span>
      )}

      {/* Badge pulse animation */}
      {badgeState.pulse && (
        <span
          className={cn(
            "absolute -top-1 -right-1 rounded-full",
            badgeSizes[size],
            badgeColors[badgeState.color],
            "animate-ping opacity-75",
          )}
        />
      )}
    </button>
  );
};

export default NotificationBadge;
